<template>
    <div class="rlms-flex rlms-items-center rlms-gap-20 rlms-fs-lg rlms-fw-600">
        <RouterLink :to="`/admin/orders`" class="rlms-flex rlms-items-center"><span class="material-symbols-outlined"> arrow_back </span></RouterLink>
        Purchase Order Details
    </div>
    <div class="detail-box rlms-border rlms-br-16 rlms-w-100">
        <div
            class="detail-box-head rlms-flex rlms-justify-between rlms-items-center rlms-border-bottom rlms-flex-wrap rlms-gap-5">
            <span class="rlms-fw-500 rlms-sm-sm">Summary</span>
            <template v-if="order?.status === 0">
                <a href="javascript:void(0);" class="rlms-btn rlms-btn-primary" @click="showModal = true;">
                    Assign PO Number
                </a>
            </template>
            <template v-if="order?.status === 2">
                <a href="javascript:void(0);" class="rlms-btn rlms-btn-primary" @click="markAsPaid">
                    Mark As Paid
                </a>
            </template>
        </div>
        <div class="detail-box-body" id="general-info-content">
            <ul class="db-ul-style-2">
                <li><span class="rlms-text-extra-3 rlms-fw-500">Purchase ID:</span>
                    <p>#{{ order?.id }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">User Email:</span>
                    <p>{{ order?.user?.email }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">Organization:</span>
                    <p>{{ order?.organization?.name }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">Product:</span>
                    <p>{{ order?.product?.name }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">Language:</span>
                    <p v-if="order?.language?.name">{{ capitalize(order?.language?.name) }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">QTY:</span>
                    <p>{{ order?.learner_quantity }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">Amount:</span>
                    <p>{{ formatCurrency(order?.amount * order?.learner_quantity) }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">Date:</span>
                    <p>{{ formatDate(order?.created_at, 'MMM d, Y') }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">PO:</span>
                    <p>{{ order?.reference_num }}</p>
                </li>
                <li><span class="rlms-text-extra-3 rlms-fw-500">Status:</span>
                    <p><span
                            :class="{ active__label: order?.status === 1, inactive__label: (order?.status == 0 || order?.status == 2) }">
                            {{ getStatusText(order?.status) }}</span></p>
                </li>
            </ul>
        </div>
    </div>
    <BaseModal :title="'Assign Purchase Order Number'" @close="closeModal" :visible="showModal" v-if="showModal">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-30">
                <div class="pp-body-scroll">
                    <div class="rlms-row rlms-row-responsive">
                        <div class="rlms-col-1">
                            <label for="email">Add PO Number</label>
                            <input type="text" placeholder="PO Number" v-model.trim="reference_num">
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && reference_numError">{{ reference_numError }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal"
                    @click="closeModal">Cancel</button>
                <button type="submit" class="rlms-btn rlms-btn-primary" id="save-org-btn"
                    @click="onSubmit">Save</button>
            </div>
        </template>
    </BaseModal>
</template>
<script setup>
import BaseModal from '@/components/BaseModal.vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { formatCurrency, formatDate } from '@/utils/format-helpers';
import { handleError } from '@/utils/handleError';
import { capitalize, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import { showConfirmDialog, showToast } from '@/utils/swal';

const order = ref(null);
const loader = useLoaderStore();

const route = useRoute();

const orderid = route.params.orderid;

const showModal = ref(false);
const submitting = ref(false);
const submitted = ref(false);

const closeModal = () => {
    showModal.value = false;
};

// Replace this with props or API fetched user
const formSchema = yup.object({
    reference_num: yup.string().required('This field is required.'),
});

// Setup form with schema
const { handleSubmit, setErrors } = useForm({
    validationSchema: formSchema,
})

// Fields
const { value: reference_num, errorMessage: reference_numError } = useField('reference_num');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;

        try {
            loader.show();

            const { data } = await api.post(`/admin/orders/${orderid}`, { ...values });
            
            order.value = data?.data ?? null;

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: 'PO number updated successfully!',
            });

            closeModal();
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

const markAsPaid = async () => {
    const result = await showConfirmDialog({
        title: 'Are you sure?',
        html: '<p id="text-message">Are you sure?</p><span class="rlms-text-danger-500" id="delete-confirm"> This action cannot be undone.</span>',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Confirm',
    });

    if (result.isConfirmed) {
        try {
            const { data } = await api.post(`/admin/orders/${orderid}`, { status: 1 });
            
            order.value = data?.data ?? null;

            showToast({
                icon: 'success',
                title: 'Updated! ',
                text: 'Order marked as paid successfully!',
            });

            closeModal();
        } catch (error) {
            handleError(error);
        }
    }
}


const getOrder = async () => {
    try {
        loader.show();

        const { data } = await api.get(`admin/orders/${orderid}`);

        order.value = data?.data ?? null;

    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const getStatusText = (status) => {
    switch (status) {
        case 0:
            return 'Pending'
        case 1:
            return 'Paid'
        case 2:
            return 'PO Number'
        default:
            return 'Unknown'
    }
}

onMounted(async () => await getOrder());

</script>